import {
  MAX_CODE_STEP_DESCRIPTION_CHARS,
  type CodeStepSaveResult,
  type ServerMessage,
} from 'ts-components/PiChat/protocol';
import type { PiChatFrameMeta } from 'ts-components/PiChat/usePiChat';
import { actions } from 'pages/AutomationEngine/store';
import type { CodeStepDetails } from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/CodeStep/types';
import {
  codeStepDetailsFromWire,
  fieldDisplayNameLookup,
} from './codeStepWire';
import { mapPersistOutcome, type LastPersist } from './persistOutcome';
import { resolveResultUpload, sendCodeStepSaveResult } from './resultUpload';

export type CodeStepSaveMessage = Extract<
  ServerMessage,
  { type: 'code_step.save' }
>;

export interface CodeStepAiContext {
  automationId: string;
  stepId: string;
}

export interface AutomationStep {
  id: string;
  type: string;
  parentKey?: string | null;
  description?: string;
  userDescription?: string | null;
  details?: unknown;
  [key: string]: unknown;
}

export interface AutomationSaveState {
  automation: { id?: string | null };
  steps: AutomationStep[];
  fields?: { id?: string; displayName?: string }[] | null;
  revision?: number | null;
  lastPersist?: LastPersist | null;
  dirty: boolean;
  editStepInfo: unknown;
  createStepInfo: unknown;
  persisting: number;
  loading: number;
  placing: unknown;
  placingVariable: unknown;
  newGotoStep: unknown;
  executionMode: unknown;
  historyId: unknown;
}

export interface AutomationStoreLike {
  getState: () => AutomationSaveState;
  dispatch: (action: any) => any;
  subscribe: (listener: () => void) => () => void;
}

export interface SaveInFlight {
  active: boolean;
}

export interface CodeStepSaveDeps {
  store: AutomationStoreLike;
  context: CodeStepAiContext;
  describe: (step: AutomationStep) => string;
  beforePersist?: () => void;
}

type Failure = Extract<CodeStepSaveResult, { ok: false }>;

const BUSY_CONDITIONS: readonly [
  keyof AutomationSaveState,
  (state: AutomationSaveState) => boolean,
][] = [
  ['dirty', (s) => Boolean(s.dirty)],
  ['editStepInfo', (s) => Boolean(s.editStepInfo)],
  ['createStepInfo', (s) => Boolean(s.createStepInfo)],
  ['persisting', (s) => s.persisting !== 0],
  ['loading', (s) => s.loading !== 0],
  ['placing', (s) => Boolean(s.placing)],
  ['placingVariable', (s) => Boolean(s.placingVariable)],
  ['newGotoStep', (s) => Boolean(s.newGotoStep)],
  ['executionMode', (s) => Boolean(s.executionMode)],
  ['historyId', (s) => Boolean(s.historyId)],
];

export const checkRefusal = (
  state: AutomationSaveState,
  message: CodeStepSaveMessage,
  context: CodeStepAiContext,
  inFlight: SaveInFlight
): Failure | null => {
  if (
    message.automationId !== context.automationId ||
    message.stepId !== context.stepId ||
    !state.automation?.id ||
    String(state.automation.id) !== message.automationId
  ) {
    return {
      ok: false,
      code: 'not_open',
      message: 'This workflow step is not the one open in the editor',
    };
  }
  if (inFlight.active) {
    return {
      ok: false,
      code: 'failed',
      message: 'Another code step save is still in progress',
    };
  }
  const step = state.steps.find(({ id }) => id === message.stepId);
  if (!step || step.type !== 'code_step') {
    return {
      ok: false,
      code: 'step_missing',
      message: 'The workflow has no code step with this id',
    };
  }
  const busy = BUSY_CONDITIONS.find(([, isBusy]) => isBusy(state));
  if (busy) {
    return {
      ok: false,
      code: 'dirty',
      message: `The workflow editor is busy or has unsaved changes (${busy[0]})`,
    };
  }
  return null;
};

export const waitForStore = <T>(
  store: AutomationStoreLike,
  select: (state: AutomationSaveState) => T | null,
  timeoutMs: number
): Promise<T | null> =>
  new Promise((resolve) => {
    const settled = select(store.getState());
    if (settled !== null) {
      resolve(settled);
      return;
    }
    const finish = (value: T | null) => {
      unsubscribe();
      clearTimeout(timer);
      resolve(value);
    };
    const unsubscribe = store.subscribe(() => {
      const value = select(store.getState());
      if (value !== null) finish(value);
    });
    const timer = setTimeout(() => finish(null), Math.max(0, timeoutMs));
  });

export const isPersistPending = (
  action: unknown
): action is {
  meta: { requestId: string };
} =>
  (
    actions.persistAutomation as unknown as {
      pending: { match: (a: unknown) => boolean };
    }
  ).pending.match(action);

export const UPLOAD_MARGIN_MS = 5_000;

const UNKNOWN_OUTCOME_FAILURE: Failure = {
  ok: false,
  code: 'failed',
  message: 'Save outcome unknown; check the workflow',
};

const EXPIRED_FAILURE: Failure = {
  ok: false,
  code: 'failed',
  message: 'The save request expired before it could be applied',
};

const GO_TO_LOOP_FAILURE: Failure = {
  ok: false,
  code: 'rejected',
  message: 'The workflow was not saved: it has a Go To Step loop',
};

export const agentDescription = (description: unknown): string | null => {
  if (typeof description !== 'string') return null;
  // Cap by code point so the cut never leaves half a surrogate pair.
  const capped = Array.from(description.trim())
    .slice(0, MAX_CODE_STEP_DESCRIPTION_CHARS)
    .join('');
  return capped.trimEnd() || null;
};

export const handleCodeStepSave = async (
  message: CodeStepSaveMessage,
  { url, isCurrent }: PiChatFrameMeta,
  { store, context, describe, beforePersist }: CodeStepSaveDeps,
  inFlight: SaveInFlight
): Promise<void> => {
  const upload = resolveResultUpload(message.upload, url);
  if (!upload || !isCurrent()) return;

  const state = store.getState();
  const refusal = checkRefusal(state, message, context, inFlight);
  if (refusal) {
    await sendCodeStepSaveResult(upload, refusal);
    return;
  }

  const waitMs = upload.expiresAt - UPLOAD_MARGIN_MS - Date.now();
  if (waitMs <= 0) {
    await sendCodeStepSaveResult(upload, EXPIRED_FAILURE);
    return;
  }

  inFlight.active = true;
  let result: CodeStepSaveResult;
  try {
    const step = state.steps.find(({ id }) => id === message.stepId)!;
    const details = codeStepDetailsFromWire(
      message.actionCodeStep,
      step.details as CodeStepDetails,
      fieldDisplayNameLookup(state.fields)
    );
    const userDescription = agentDescription(message.description);
    const next = {
      ...step,
      details,
      ...(userDescription !== null && { userDescription }),
    };
    const description = describe(next);
    store.dispatch(actions.editStep({ step: { ...next, description } }));
    beforePersist?.();
    const started = store.dispatch(actions.persistAutomation());
    if (!isPersistPending(started)) {
      result = GO_TO_LOOP_FAILURE;
    } else {
      const { requestId } = started.meta;
      const last = await waitForStore(
        store,
        ({ lastPersist }) =>
          lastPersist?.requestId === requestId ? lastPersist : null,
        waitMs
      );
      result = last
        ? mapPersistOutcome(last, store.getState().revision, message.stepId)
        : UNKNOWN_OUTCOME_FAILURE;
    }
  } catch (error) {
    result = {
      ok: false,
      code: 'failed',
      message: error instanceof Error ? error.message : String(error),
    };
  } finally {
    inFlight.active = false;
  }
  await sendCodeStepSaveResult(upload, result);
};
